import { useEffect, useRef, useState } from "react";
import { Outlet, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import Spinner from "../../../components/ui/spinners/CompactLoadingSpinner";
import { fetchFullCaseData } from "../../../thunks/FullCaseData";
import AccessDenied from "../../auth/components/AccessDenied";
import { selectCaseHeader, selectCaseHeaderLoadingStatus } from "../../caseinfo/logic/Slice";
import DocumentPanel from "../../documentManagement/components/DocumentPanel";
import UploadDocumentModal from "../../documentManagement/components/uploadDocumentModal";
import { DOCUMENT_PANEL_VISIBLE_TYPES } from "../../documentManagement/config/DocumentPanelVisibleType";
import CaseNavigationSidebar from "../../sidebar/CaseSidebar";
import { AddEventModal } from "../../timeline/components/AddEventModal";
import { TimelinePanel } from "../../timeline/components/TimelinePanel";
import CaseSidebar from "./CaseSideBar";
import CaseSidebarFooter from "./CaseSideBarFooterButton";
import CaseSidePanel from "./CaseSidePanel";
import { Can } from "../../auth/components/Can";
import { Permission, useAbility } from "../../auth/logic/rbac";


export enum SidebarItem {
  Documents = "documents",
  Insights = "insights",
  CaseTimeline = "caseTimeline",
}

export default function CaseLayout() {

  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { can } = useAbility();

  useEffect(() => {
      if(caseId) {
          dispatch(fetchFullCaseData({ caseId,canAccessInvestigation: can(Permission.ACCESS_INVESTIGATION)}));
      }
  }, [caseId, dispatch])

  const caseInfoLoadingStatus = useAppSelector(selectCaseHeaderLoadingStatus)
  const caseData = useAppSelector(selectCaseHeader);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isAddEventModalOpen, setIsAddEventModalOpen] = useState(false);
  const [selectedSidebarItem, setSelectedSidebarItem] =
    useState<SidebarItem | null>(null);

  const handleToggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const handleSelectSidebarItem = (item: SidebarItem) => {
    setSelectedSidebarItem(item);
  };

  const handleClosePanel = () => {
    setSelectedSidebarItem(null);
  };

  if(caseInfoLoadingStatus==='pending') {
    return <Spinner isFixed message='Loading...' />
  }

  if(caseInfoLoadingStatus==='succeeded' && caseData === null) {
    return <AccessDenied />
  }

  return (
    <div className="w-full flex h-full overflow-hidden">
        <CaseNavigationSidebar />
      <div  ref={scrollRef} className="flex-1 min-w-0 h-full overflow-y-auto">
        <Outlet  context={{ scrollRef }}/>
      </div>
      <Can perform={Permission.ACCESS_CASE_RIGHT_SIDEBAR} variant="hide">
        {selectedSidebarItem === SidebarItem.Documents && (
          <CaseSidePanel
            headerName="Documents"
            addEnabled={true}
            onAdd={() => setIsUploadModalOpen(true)}
            onClose={handleClosePanel}
            isSidebarOpen={isSidebarOpen}
          >
            <DocumentPanel />
          </CaseSidePanel>
        )}

        {selectedSidebarItem === SidebarItem.CaseTimeline && (
          <CaseSidePanel
            headerName="Case Timeline"
            addEnabled={true}
            onClose={handleClosePanel}
            isSidebarOpen={isSidebarOpen}
            onAdd={() => setIsAddEventModalOpen(true)}
          >
          <TimelinePanel/>  
          </CaseSidePanel>
        )}

        {selectedSidebarItem === SidebarItem.Insights && (
          <CaseSidePanel
            headerName="AI Insights"
            addEnabled={false}
            onClose={handleClosePanel}
            isSidebarOpen={isSidebarOpen}
            enableExpansion={true}
          ></CaseSidePanel>
        )}

        {isSidebarOpen && (
          <CaseSidebar
            selected={selectedSidebarItem}
            onSelect={handleSelectSidebarItem}
          />
        )}

        <CaseSidebarFooter
          isSidebarOpen={isSidebarOpen}
          onToggle={handleToggleSidebar}
        />

        {isUploadModalOpen && (
          <UploadDocumentModal
            onClose={() => setIsUploadModalOpen(false)}
            allowedDocumentTypes={DOCUMENT_PANEL_VISIBLE_TYPES}
          />
        )}

        {isAddEventModalOpen && (
          <AddEventModal
            mode="Add"
            onClose={() => setIsAddEventModalOpen(false)}
          />
        )}

      </Can>    

    </div>
  );
}
